import React from 'react';
import { formatNumber } from './numberUtils';

export const formatDataValue = (
  value: any,
  type: string,
  digits = 0,
  attach = '',
): string | React.ReactElement => {
  if (type === 'number') {
    // undefined/NaN ist „kein Wert" (z. B. Wandlungsrate eines Einmalprodukts) — nie „NaN" anzeigen.
    if (value === undefined || (typeof value === 'number' && Number.isNaN(value))) return '–';
    return formatNumber(value, digits) + attach;
  } else if (type === 'percentage') {
    return formatNumber(value * 100, digits) + attach;
  } else if (type === 'date') {
    if (!value) return '';
    return new Date(value).toLocaleDateString('de-DE');
  } else if (type === 'datetime') {
    // Fuer Spalten mit ECHTER UTC-Zeit (z. B. LPS-Klon process.created): der Browser rechnet
    // nach Berlin um, und das ist dort richtig.
    if (!value) return '';
    return new Date(value).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } else if (type === 'datetime-wallclock') {
    // Fuer Spalten, die Berliner WALLCLOCK tragen (die meisten DB14-Spalten, z. B.
    // plx_all_orders.created_on). mysql2 liefert sie mit Z-Suffix, obwohl der Zahlenwert schon
    // Berlin ist — 'datetime' rechnete sie deshalb ein zweites Mal um und stand 2 h zu spaet.
    // Die Zeitzone ist per SPALTE festgelegt, nicht per Tabelle: CLAUDE.md § Timezones.
    if (!value) return '';
    return new Date(value).toLocaleString('de-DE', {
      timeZone: 'UTC',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } else if (type === 'custom') {
    // For custom types, return the value as-is (will be handled by TableWidget)
    return value;
  } else {
    return String(value);
  }
};

/**
 * Wie `formatDataValue`, aber fuer die SUMMENZEILE einer Tabelle (`TableWidget`-Footer).
 *
 * Eine Summenzeile hat fuer viele Spalten schlicht keinen Wert — eine Bestellnummer, eine
 * E-Mail-Adresse oder ein Datum laesst sich nicht summieren. `formatDataValue` macht daraus
 * „null", „NaN" oder „Invalid Date" (gemessen 2026-09-17: 8 von 32 Tabellen mit Summenzeile,
 * 2 weitere bei leeren Daten). Leer ist dort die ehrliche Ausgabe.
 *
 * Das Label ist der Sonderfall: mehrere Seiten schreiben „Insgesamt" in ihre erste Spalte, und die
 * ist je nach Tabelle ein Datum (Doi-/Pdf-Zone, Push-Sends) oder eine Zahl (Titel-Wortzahl). Ein
 * Text in einer solchen Spalte wird als Label ausgegeben, nicht als „Invalid Date" oder „NaN".
 *
 * Bewusst NUR im Footer — in Datenzeilen bleibt `null` weiter eine 0, sonst aendert sich die
 * Bedeutung bestehender Tabellen.
 */
export const formatSummaryValue = (
  value: any,
  type: string,
  digits = 0,
  attach = '',
): string | React.ReactElement => {
  if (value === null || value === undefined || value === '') return '';

  if (type === 'number' || type === 'percentage') {
    if (!Number.isFinite(Number(value))) return typeof value === 'string' ? value : '';
    return formatDataValue(value, type, digits, attach);
  }

  const rendered = formatDataValue(value, type, digits, attach);
  if (typeof rendered === 'string' && rendered.includes('Invalid Date')) {
    return typeof value === 'string' ? value : '';
  }
  return rendered;
};

/**
 * Formats a German currency value with invoice count in brackets
 * Example: formatGermanCurrency(731.50, 3) => "731,50 (3)"
 */
export const formatGermanCurrency = (
  amount: number | null,
  invoiceCount: number | null,
): string => {
  if (amount === null || amount === undefined) {
    return '0,00';
  }

  const formattedAmount = formatNumber(amount, 2);

  if (invoiceCount !== null && invoiceCount !== undefined && invoiceCount > 0) {
    return `${formattedAmount} (${invoiceCount})`;
  }

  return formattedAmount;
};

/**
 * Smart formatting for lifetime days
 * - > 365 days: show as "X,X Jahre"
 * - > 30 days: show as "X Monate"
 * - <= 30 days: show as "X Tage"
 */
export const formatLifetime = (days: number | null): string => {
  if (days === null || days === undefined || days < 0) {
    return '0 Tage';
  }

  if (days >= 365) {
    const years = days / 365;
    return `${formatNumber(years, 1)} Jahre`;
  } else if (days > 30) {
    const months = Math.floor(days / 30);
    return `${months} Monate`;
  } else {
    return `${days} Tage`;
  }
};
